import { RiInformation2Line } from '@remixicon/react';
import { useT } from '@transifex/react';
import type { ComponentProps, ReactNode } from 'react';

import { type Size, cn } from '@playbook/utils';

import css from './styles.module.css';

export type FormLabelSize = Extract<Size, 'sm' | 'md'>;

const DefaultFormLabelSize: FormLabelSize = 'md' as const;

type FormLabelEventHandlers = {
	[K in keyof ComponentProps<'div'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'div'>[K];
};

interface FormLabelDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface FormLabelProperties
	extends FormLabelEventHandlers, FormLabelDataAttributes {
	size?: FormLabelSize;
	optional?: boolean;
	required?: boolean;

	htmlFor: string;
	label: string;
	description?: string;
	help?: string;
}

/**
 *	The description sits outside the `<label>` on purpose. A label's accessible
 *	name is its whole subtree, so a description inside it is read out as part of
 *	the field's name on every focus rather than as the description it is. It
 *	carries `<htmlFor>-description` instead, for whoever renders the field to
 *	point `aria-describedby` at — `atoms/inputs/text-area` builds the same
 *	string.
 *
 *	The help icon is hidden from assistive software for the same reason, and
 *	carries its text as a `title` so it is still announced, as a description.
 *
 *	The `<label>` carries `<htmlFor>-label` so a control that `htmlFor` cannot
 *	reach can point `aria-labelledby` at it instead. `htmlFor` only names the
 *	labelable elements HTML-AAM maps it onto, and a `<button>` is not one of
 *	them — its accessible name comes from its own contents. That is what
 *	`modules/dropdown` needs, since its trigger is a button.
 */
export function FormLabel({
	size = DefaultFormLabelSize,
	optional = false,
	required = false,

	htmlFor,
	label,
	description,
	help,

	...props
}: FormLabelProperties): ReactNode {
	const t = useT();
	const className: string = cn(css.label, css[size], required && css.required);

	return (
		<div
			{...props}
			data-size={size}
			data-required={required}
			className={className}>
			<label id={`${htmlFor}-label`} htmlFor={htmlFor} className={css.row}>
				<span className={css.text}>{label}</span>
				{optional && <span className={css.optional}>{t('Optional')}</span>}
				{help && (
					<span aria-hidden="true" title={help} className={css.help}>
						<RiInformation2Line />
					</span>
				)}
			</label>
			{description && (
				<span id={`${htmlFor}-description`} className={css.description}>
					{description}
				</span>
			)}
		</div>
	);
}
